import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAccountQueryTemplates,
  createQueryTemplate,
  updateQueryTemplate,
  deleteQueryTemplate,
  executeQuery,
  sortResults,
  filterResults,
  exportToCSV,
  printResults,
  getAvailableCollections,
  getOperators,
  getAggregationTypes,
  subscribeToQueryTemplates
} from '../services/dataQuery';
import './DataQuery.css';

function DataQuery() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [queryResults, setQueryResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Sort and filter state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection: 'timers',
    conditions: [],
    orderBy: [],
    groupBy: {
      enabled: false,
      field: '',
      levels: 1,
      aggregations: []
    },
    displayFields: [],
    limit: 100
  });

  const collections = getAvailableCollections();
  const operators = getOperators();
  const aggregationTypes = getAggregationTypes();

  useEffect(() => {
    if (accountId) {
      loadTemplates();

      const unsubscribe = subscribeToQueryTemplates(accountId, (templatesData) => {
        setTemplates(templatesData);
      });

      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [accountId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templatesData = await getAccountQueryTemplates(accountId);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      alert('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('請輸入查詢名稱');
      return;
    }

    try {
      const template = await createQueryTemplate({
        ...formData,
        accountId: accountId || 'default'
      });
      alert('查詢範本已創建');
      resetForm();
      setShowTemplateModal(false);
    } catch (err) {
      console.error('Error creating template:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();

    if (!editingTemplate) return;

    try {
      await updateQueryTemplate(editingTemplate.id, formData);
      alert('查詢範本已更新');
      resetForm();
      setEditingTemplate(null);
    } catch (err) {
      console.error('Error updating template:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`確定要刪除查詢範本「${templateName}」嗎？`)) return;

    try {
      await deleteQueryTemplate(templateId);
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setQueryResults([]);
      }
      alert('查詢範本已刪除');
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleExecuteQuery = async (template) => {
    try {
      setExecuting(true);
      setSelectedTemplate(template);
      const result = await executeQuery(template);
      setQueryResults(result.results);
      setSortField('');
      setSortDirection('asc');
      setFilterField('');
      setFilterValue('');
    } catch (err) {
      console.error('Error executing query:', err);
      alert('執行查詢失敗：' + err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      collection: template.collection,
      conditions: template.conditions || [],
      orderBy: template.orderBy || [],
      groupBy: template.groupBy || {
        enabled: false,
        field: '',
        levels: 1,
        aggregations: []
      },
      displayFields: template.displayFields || [],
      limit: template.limit || 100
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      collection: 'timers',
      conditions: [],
      orderBy: [],
      groupBy: {
        enabled: false,
        field: '',
        levels: 1,
        aggregations: []
      },
      displayFields: [],
      limit: 100
    });
  };

  const handleAddCondition = () => {
    setFormData({
      ...formData,
      conditions: [
        ...formData.conditions,
        { field: '', operator: '==', value: '' }
      ]
    });
  };

  const handleRemoveCondition = (index) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_, i) => i !== index)
    });
  };

  const handleConditionChange = (index, key, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index][key] = value;
    setFormData({ ...formData, conditions: newConditions });
  };

  const handleAddOrderBy = () => {
    setFormData({
      ...formData,
      orderBy: [
        ...formData.orderBy,
        { field: '', direction: 'asc' }
      ]
    });
  };

  const handleRemoveOrderBy = (index) => {
    setFormData({
      ...formData,
      orderBy: formData.orderBy.filter((_, i) => i !== index)
    });
  };

  const handleOrderByChange = (index, key, value) => {
    const newOrderBy = [...formData.orderBy];
    newOrderBy[index][key] = value;
    setFormData({ ...formData, orderBy: newOrderBy });
  };

  const handleAddAggregation = () => {
    setFormData({
      ...formData,
      groupBy: {
        ...formData.groupBy,
        aggregations: [
          ...formData.groupBy.aggregations,
          { type: 'sum', field: '' }
        ]
      }
    });
  };

  const handleRemoveAggregation = (index) => {
    setFormData({
      ...formData,
      groupBy: {
        ...formData.groupBy,
        aggregations: formData.groupBy.aggregations.filter((_, i) => i !== index)
      }
    });
  };

  const handleAggregationChange = (index, key, value) => {
    const newAggregations = [...formData.groupBy.aggregations];
    newAggregations[index][key] = value;
    setFormData({
      ...formData,
      groupBy: {
        ...formData.groupBy,
        aggregations: newAggregations
      }
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => {
    const dataToExport = getFilteredAndSortedResults();
    exportToCSV(dataToExport, `query_result_${new Date().getTime()}.csv`);
  };

  const handlePrint = () => {
    const dataToPrint = getFilteredAndSortedResults();
    printResults(dataToPrint);
  };

  const getFilteredAndSortedResults = () => {
    let data = [...queryResults];

    // Apply filter
    if (filterField && filterValue) {
      data = filterResults(data, filterField, filterValue);
    }

    // Apply sort
    if (sortField) {
      data = sortResults(data, sortField, sortDirection);
    }

    return data;
  };

  const displayedResults = getFilteredAndSortedResults();
  const resultFields = queryResults.length > 0 ? Object.keys(queryResults[0]) : [];

  if (loading) {
    return (
      <div className="data-query">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="data-query">
      <header className="page-header">
        <div className="header-left">
          <h1>數據查詢</h1>
          <p>自定義查詢條件與結果展示</p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowTemplateModal(true);
            }}
          >
            ➕ 新增查詢範本
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            返回儀表板
          </button>
        </div>
      </header>

      <div className="query-container">
        {/* Templates Sidebar */}
        <div className="templates-sidebar">
          <h2>查詢範本</h2>
          {templates.length === 0 ? (
            <div className="empty-templates">
              <p>尚無查詢範本</p>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  resetForm();
                  setShowTemplateModal(true);
                }}
              >
                創建第一個範本
              </button>
            </div>
          ) : (
            <div className="templates-list">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`template-item ${selectedTemplate?.id === template.id ? 'active' : ''}`}
                >
                  <div className="template-info" onClick={() => setSelectedTemplate(template)}>
                    <h4>{template.name}</h4>
                    <p>{template.description}</p>
                    <div className="template-meta">
                      <span className="collection-tag">{template.collection}</span>
                      {template.conditions && template.conditions.length > 0 && (
                        <span className="condition-count">{template.conditions.length} 條件</span>
                      )}
                    </div>
                  </div>
                  <div className="template-actions">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleExecuteQuery(template)}
                      disabled={executing}
                    >
                      ▶️
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleEditTemplate(template)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteTemplate(template.id, template.name)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="results-panel">
          {executing ? (
            <div className="loading-results">
              <div className="spinner"></div>
              <p>執行查詢中...</p>
            </div>
          ) : queryResults.length === 0 ? (
            <div className="empty-results">
              <h3>尚無查詢結果</h3>
              <p>請選擇查詢範本並執行</p>
            </div>
          ) : (
            <>
              <div className="results-header">
                <div className="results-info">
                  <h3>{selectedTemplate?.name}</h3>
                  <p>共 {displayedResults.length} 筆結果</p>
                </div>
                <div className="results-actions">
                  <button className="btn btn-sm btn-secondary" onClick={handleExport}>
                    📥 匯出 CSV
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={handlePrint}>
                    🖨️ 列印
                  </button>
                </div>
              </div>

              <div className="results-filters">
                <div className="filter-group">
                  <select
                    value={filterField}
                    onChange={(e) => setFilterField(e.target.value)}
                  >
                    <option value="">選擇篩選欄位...</option>
                    {resultFields.map(field => (
                      <option key={field} value={field}>{field}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="篩選值..."
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    disabled={!filterField}
                  />
                </div>
              </div>

              <div className="results-table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      {resultFields.map(field => (
                        <th key={field} onClick={() => handleSort(field)}>
                          {field}
                          {sortField === field && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map((row, idx) => (
                      <tr key={idx}>
                        {resultFields.map(field => (
                          <td key={field}>
                            {typeof row[field] === 'object'
                              ? JSON.stringify(row[field])
                              : String(row[field] || '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {(showTemplateModal || editingTemplate) && (
        <div className="modal-overlay" onClick={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}>
          <div className="modal-content query-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTemplate ? '編輯查詢範本' : '新增查詢範本'}</h2>
              <button className="close-btn" onClick={() => {
                setShowTemplateModal(false);
                setEditingTemplate(null);
              }}>
                ✕
              </button>
            </div>

            <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="query-form">
              <div className="form-section">
                <h3>基本資訊</h3>
                <div className="form-group">
                  <label>查詢名稱 *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="例如: 進行中的計時器"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>描述</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="查詢用途說明"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>資料集合 *</label>
                  <select
                    value={formData.collection}
                    onChange={(e) => setFormData({ ...formData, collection: e.target.value })}
                  >
                    {collections.map(col => (
                      <option key={col.value} value={col.value}>{col.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>查詢條件</h3>
                {formData.conditions.map((condition, idx) => (
                  <div key={idx} className="condition-row">
                    <input
                      type="text"
                      placeholder="欄位名稱"
                      value={condition.field}
                      onChange={(e) => handleConditionChange(idx, 'field', e.target.value)}
                    />
                    <select
                      value={condition.operator}
                      onChange={(e) => handleConditionChange(idx, 'operator', e.target.value)}
                    >
                      {operators.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="值"
                      value={condition.value}
                      onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveCondition(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={handleAddCondition}
                >
                  ➕ 新增條件
                </button>
              </div>

              <div className="form-section">
                <h3>排序</h3>
                {formData.orderBy.map((order, idx) => (
                  <div key={idx} className="order-row">
                    <input
                      type="text"
                      placeholder="欄位名稱"
                      value={order.field}
                      onChange={(e) => handleOrderByChange(idx, 'field', e.target.value)}
                    />
                    <select
                      value={order.direction}
                      onChange={(e) => handleOrderByChange(idx, 'direction', e.target.value)}
                    >
                      <option value="asc">升冪</option>
                      <option value="desc">降冪</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveOrderBy(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={handleAddOrderBy}
                >
                  ➕ 新增排序
                </button>
              </div>

              <div className="form-section">
                <h3>分組與統計</h3>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.groupBy.enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        groupBy: { ...formData.groupBy, enabled: e.target.checked }
                      })}
                    />
                    <span>啟用分組</span>
                  </label>
                </div>

                {formData.groupBy.enabled && (
                  <>
                    <div className="form-group">
                      <label>分組欄位</label>
                      <input
                        type="text"
                        placeholder="欄位名稱"
                        value={formData.groupBy.field}
                        onChange={(e) => setFormData({
                          ...formData,
                          groupBy: { ...formData.groupBy, field: e.target.value }
                        })}
                      />
                    </div>

                    <h4>統計函數</h4>
                    {formData.groupBy.aggregations.map((agg, idx) => (
                      <div key={idx} className="aggregation-row">
                        <select
                          value={agg.type}
                          onChange={(e) => handleAggregationChange(idx, 'type', e.target.value)}
                        >
                          {aggregationTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="欄位名稱"
                          value={agg.field}
                          onChange={(e) => handleAggregationChange(idx, 'field', e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveAggregation(idx)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleAddAggregation}
                    >
                      ➕ 新增統計
                    </button>
                  </>
                )}
              </div>

              <div className="form-section">
                <h3>其他設定</h3>
                <div className="form-group">
                  <label>結果數量限制</label>
                  <input
                    type="number"
                    value={formData.limit}
                    onChange={(e) => setFormData({ ...formData, limit: parseInt(e.target.value) })}
                    min="1"
                    max="1000"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTemplate ? '更新' : '創建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataQuery;
